import { availabilityRank } from './attendanceSort'

export interface AvailabilityOrderablePlayer {
  id: string
  attendance_status?: number | null
}

/**
 * Freeze the current availability ordering as `id → position`.
 *
 * Availability grouping is applied from a SNAPSHOT rather than live values so a
 * row doesn't jump out from under the user the instant they set its status.
 * The snapshot is taken when the list first loads (and again whenever the user
 * asks for a re-sort); until then, a status change updates the icon in place.
 */
export const buildAvailabilityOrder = (
  players: AvailabilityOrderablePlayer[] = []
): Record<string, number> => {
  const order: Record<string, number> = {}
  ;[...players]
    .sort(
      (a, b) =>
        availabilityRank(a.attendance_status) -
        availabilityRank(b.attendance_status)
    )
    .forEach((player, index) => {
      order[player.id] = index
    })
  return order
}

/**
 * Comparator that orders by a frozen snapshot, falling back to `compare` for
 * rows absent from it (e.g. a player added after the snapshot was taken —
 * those sort last, in the column order, rather than at an arbitrary position).
 */
export const bySnapshotOrder =
  <T extends AvailabilityOrderablePlayer>(
    order: Record<string, number>,
    compare: (a: T, b: T) => number
  ) =>
  (a: T, b: T): number => {
    const aPos = order[a.id]
    const bPos = order[b.id]
    const aMissing = aPos === undefined
    const bMissing = bPos === undefined

    if (aMissing && bMissing) {
      return compare(a, b)
    }
    if (aMissing) {
      return 1
    }
    if (bMissing) {
      return -1
    }
    return aPos - bPos
  }
